import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../components/Input';
import Button from '../components/Button';
import { organizerEventsService } from '../services/organizerEventsService';
import { describeEventSaveError, parseEventSaveError } from '../utils/eventSaveErrors';

interface Stage {
    id: number | string; // Puede ser string (UUID backend) o number (timestamp frontend)
    name: string;
    price: string;
    capacity: string;
    courtesy: string;
    status: 'active' | 'inactive' | 'sold_out';
}

const CATEGORIES = [
    { id: 'CONCERT', label: '🎵 Concierto' },
    { id: 'FESTIVAL', label: '🎉 Festival' },
    { id: 'THEATER', label: '🎭 Teatro' },
    { id: 'STANDUP', label: '🎤 Stand-up' },
    { id: 'SPORTS', label: '🏅 Deportes' },
    { id: 'ART', label: '🎨 Arte' },
    { id: 'FAMILY', label: '👨‍👩‍👧‍👦 Familiar' },
    { id: 'CONFERENCE', label: '💼 Conferencia' },
    { id: 'PARTY', label: '🥳 Fiesta' },
    { id: 'OTHER', label: '🎫 Otro' },
];

const OrganizerCreateEditEvent = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Estado del formulario
    const [eventData, setEventData] = useState({
        name: '',
        description: '',
        date: '',
        endDate: '',
        basePrice: '',
        location: '',
        contactPhone: '',
        refundEmail: '',
        refundPhone: '',
        termsAndConditions: '',
        category: '',
        status: 'DRAFT'
    });

    const [flyerFile, setFlyerFile] = useState<File | null>(null);
    const [flyerPreview, setFlyerPreview] = useState<string | null>(null);

    const [stages, setStages] = useState<Stage[]>([
        { id: Date.now(), name: '', price: '', capacity: '', courtesy: '0', status: 'active' }
    ]);

    // --- EFECTO: CARGAR DATOS EN EDICIÓN ---
    useEffect(() => {
        if (isEditMode && id) {
            const loadEvent = async () => {
                setIsLoadingData(true);
                try {
                    const data = await organizerEventsService.getEventById(id);

                    // Formatear fecha para input datetime-local (YYYY-MM-DDTHH:mm)
                    // El backend suele enviar segundos, debemos cortarlos si existen para que el input lo lea
                    const formattedDate = data.startDate ? data.startDate.substring(0, 16) : '';

                    setEventData({
                        name: data.name,
                        description: data.description,
                        date: formattedDate,
                        endDate: data.endDate ? data.endDate.substring(0, 16) : '',
                        basePrice: data.referencePriceFrom.toString(),
                        location: data.location,
                        contactPhone: data.whatsappContact,
                        refundEmail: data.refundContactEmail ?? '',
                        refundPhone: data.refundContactPhone ?? '',
                        termsAndConditions: data.termsConditions,
                        category: data.category,
                        status: data.status // 'ACTIVE' o 'DRAFT'
                    });

                    if (data.imageUrl) {
                        setFlyerPreview(data.imageUrl);
                    }

                    if (data.stages && data.stages.length > 0) {
                        const mappedStages = data.stages.map((s: any) => ({
                            id: s.id, // ID real del backend
                            name: s.name,
                            price: s.price.toString(),
                            capacity: s.totalQuantity.toString(),
                            courtesy: s.courtesyQuantity.toString(),
                            status: s.isActive ? 'active' : 'inactive'
                        }));
                        setStages(mappedStages);
                    }

                } catch (error) {
                    console.error("Error cargando evento:", error);
                    alert("No se pudo cargar la información del evento.");
                    navigate('/organizer/dashboard');
                } finally {
                    setIsLoadingData(false);
                }
            };
            loadEvent();
        }
    }, [isEditMode, id, navigate]);

    // Cleanup de preview
    useEffect(() => {
        return () => {
            if (flyerPreview && flyerPreview.startsWith('blob:')) {
                URL.revokeObjectURL(flyerPreview);
            }
        };
    }, [flyerPreview]);

    // --- HANDLERS ---
    const handleEventChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEventData(prev => ({ ...prev, [name]: value }));
    };

    const toggleStatus = () => {
        setEventData(prev => ({
            ...prev,
            status: prev.status === 'DRAFT' ? 'ACTIVE' : 'DRAFT'
        }));
    };

    const handleFlyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
                alert('Solo se permiten archivos JPG o PNG');
                return;
            }
            setFlyerFile(file);
            const objectUrl = URL.createObjectURL(file);
            setFlyerPreview(objectUrl);
        }
    };

    const addStage = () => {
        setStages(prev => [
            ...prev,
            { id: Date.now(), name: '', price: '', capacity: '', courtesy: '0', status: 'active' }
        ]);
    };

    const removeStage = (stageId: number | string) => {
        if (stages.length === 1) {
            alert("Debes tener al menos una etapa creada.");
            return;
        }
        setStages(prev => prev.filter(stage => stage.id !== stageId));
    };

    const updateStage = (stageId: number | string, field: keyof Stage, value: string) => {
        setStages(prev => prev.map(stage => {
            if (stage.id === stageId) {
                return { ...stage, [field]: value };
            }
            return stage;
        }));
    };

    // --- SUBMIT ---
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validaciones básicas
        if (!isEditMode && !flyerFile) {
            alert("Por favor selecciona una imagen para el flyer.");
            return;
        }
        if (isEditMode && !flyerFile && !flyerPreview) {
            alert("El evento debe tener una imagen.");
            return;
        }
        if (!eventData.category) {
            alert("Por favor selecciona una categoría.");
            return;
        }
        if (eventData.endDate && eventData.date && eventData.endDate <= eventData.date) {
            alert("La fecha de finalización debe ser posterior a la fecha de inicio.");
            return;
        }
        // Courtesy tickets come out of the capacity: they are not sold
        for (const stage of stages) {
            const capacity = Number(stage.capacity);
            const courtesy = Number(stage.courtesy || 0);
            if (!(capacity >= 1)) {
                alert(`El aforo de la etapa "${stage.name}" debe ser de al menos 1.`);
                return;
            }
            if (!(courtesy >= 0) || courtesy > capacity) {
                alert(`Las cortesías de la etapa "${stage.name}" deben estar entre 0 y su aforo.`);
                return;
            }
        }
        // Publishing needs the end date (sales close then) and a refund contact (SuperTicket does not handle refunds)
        if (eventData.status === 'ACTIVE') {
            if (!eventData.endDate) {
                alert("Para publicar el evento indica la fecha y hora de finalización.");
                return;
            }
            if (!eventData.refundEmail.trim() && !eventData.refundPhone.trim()) {
                alert("Para publicar el evento indica un correo o un teléfono para reembolsos.");
                return;
            }
        }

        setIsSaving(true);

        try {
            // Aseguramos que la fecha tenga segundos si el input no los puso
            const finalDate = eventData.date.length === 16 ? `${eventData.date}:00` : eventData.date;
            const finalEndDate = !eventData.endDate ? null
                : eventData.endDate.length === 16 ? `${eventData.endDate}:00` : eventData.endDate;

            const payload = {
                name: eventData.name,
                startDate: finalDate,
                endDate: finalEndDate,
                location: eventData.location,
                referencePriceFrom: Number(eventData.basePrice),
                whatsappContact: eventData.contactPhone,
                refundContactEmail: eventData.refundEmail.trim() || null,
                refundContactPhone: eventData.refundPhone.trim() || null,
                description: eventData.description,
                termsConditions: eventData.termsAndConditions,
                category: eventData.category,
                status: eventData.status,
                stages: stages.map(stage => ({
                    // Si es string (UUID) es existente, si es number (Date.now) es nuevo
                    id: typeof stage.id === 'string' ? stage.id : null,
                    name: stage.name,
                    price: Number(stage.price),
                    totalQuantity: Number(stage.capacity),
                    courtesyQuantity: Number(stage.courtesy || 0),
                    isActive: stage.status === 'active'
                }))
            };

            if (isEditMode && id) {
                await organizerEventsService.updateEvent(id, payload, flyerFile);
                alert("¡Evento actualizado exitosamente!");
            } else {
                await organizerEventsService.createEvent(payload, flyerFile!);
                alert("¡Evento creado exitosamente!");
            }

            navigate('/organizer/dashboard');

        } catch (error) {
            console.error("Error saving event:", error);
            // The backend explains validation errors in a JSON "message", and stage changes it rejects with a "code"
            const body = parseEventSaveError(error);
            const detail = describeEventSaveError(body);
            alert(body.code
                ? `No se guardaron los cambios.\n\n${detail}`
                : "Hubo un error al guardar el evento. Verifica los datos." + (detail ? `\n\n${detail}` : ''));
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoadingData) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="st-spinner st-spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="st-page py-12">
            <div className="st-container max-w-5xl">

                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">
                        {isEditMode ? 'Editar Evento' : 'Crear Nuevo Evento'}
                    </h1>
                    <p className="text-[var(--st-text-secondary)]">
                        {isEditMode
                            ? 'Modifica la información de tu evento y actualiza los cambios.'
                            : 'Diligencia la información básica y define las etapas de venta.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">

                    {/* TARJETA 1: INFO GENERAL */}
                    <div className="st-card-static p-6 md:p-8">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-[var(--st-border)] pb-4">

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--st-accent-primary-light)] text-[var(--st-accent-primary)] flex items-center justify-center font-bold">1</div>
                                <h2 className="font-display text-xl font-bold">Información del Evento</h2>
                            </div>

                            <div
                                className="flex items-center gap-3 cursor-pointer group bg-[var(--st-bg-tertiary)] px-4 py-2 rounded-xl transition-colors hover:bg-[var(--st-bg-secondary)] border border-transparent hover:border-[var(--st-border)]"
                                onClick={toggleStatus}
                            >
                                <span className={`text-sm font-semibold transition-colors ${eventData.status === 'ACTIVE' ? 'text-[var(--st-success)]' : 'text-[var(--st-text-muted)]'}`}>
                                    {eventData.status === 'ACTIVE'
                                        ? (isEditMode ? 'Evento Activo' : 'Publicar Ahora')
                                        : (isEditMode ? 'Evento en Borrador' : 'Guardar como Borrador')}
                                </span>
                                <div className={`st-toggle st-toggle-sm ${eventData.status === 'ACTIVE' ? 'active' : ''}`}></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Nombre */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required">Nombre del Evento</label>
                                <Input
                                    name="name"
                                    value={eventData.name}
                                    onChange={handleEventChange}
                                    placeholder="Ej: Festival de Verano 2026"
                                    required
                                />
                            </div>

                            {/* Categoría */}
                            <div>
                                <label className="st-label st-label-required">Categoría</label>
                                <select
                                    name="category"
                                    className="st-input st-select w-full"
                                    value={eventData.category}
                                    onChange={handleEventChange}
                                    required
                                >
                                    <option value="">Selecciona una opción...</option>
                                    {CATEGORIES.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Fecha de inicio */}
                            <div>
                                <label className="st-label st-label-required">Fecha y Hora de Inicio</label>
                                <Input
                                    name="date"
                                    type="datetime-local"
                                    value={eventData.date}
                                    onChange={handleEventChange}
                                    required
                                />
                            </div>

                            {/* Fecha de finalización: las ventas se cierran en este momento */}
                            <div>
                                <label className={`st-label ${eventData.status === 'ACTIVE' ? 'st-label-required' : ''}`}>Fecha y Hora de Finalización</label>
                                <Input
                                    name="endDate"
                                    type="datetime-local"
                                    value={eventData.endDate}
                                    onChange={handleEventChange}
                                    min={eventData.date || undefined}
                                    required={eventData.status === 'ACTIVE'}
                                />
                                <p className="text-xs text-[var(--st-text-muted)] mt-1">Las ventas se cierran en este momento.</p>
                            </div>

                            {/* Ubicación */}
                            <div>
                                <label className="st-label st-label-required">Ubicación / Lugar</label>
                                <Input
                                    name="location"
                                    value={eventData.location}
                                    onChange={handleEventChange}
                                    placeholder="Ej: Teatro Mayor, Bogotá"
                                    required
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                                />
                            </div>

                            {/* Precio Base */}
                            <div>
                                <label className="st-label st-label-required">Valor Referencia (Desde)</label>
                                <Input
                                    name="basePrice"
                                    type="number"
                                    value={eventData.basePrice}
                                    onChange={handleEventChange}
                                    placeholder="0"
                                    required
                                    icon={<span className="font-bold text-sm text-[var(--st-text-muted)]">$</span>}
                                />
                            </div>

                            {/* Contacto */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required">WhatsApp de Contacto</label>
                                <Input
                                    name="contactPhone"
                                    type="tel"
                                    value={eventData.contactPhone}
                                    onChange={handleEventChange}
                                    placeholder="Ej: 3001234567"
                                    required
                                    icon={<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                                />
                            </div>

                            {/* Contacto para reembolsos: SuperTicket no gestiona reembolsos */}
                            <div className="md:col-span-2">
                                <p className="st-label">Contacto para Reembolsos</p>
                                <p className="text-xs text-[var(--st-text-muted)] mb-3">
                                    Los compradores solicitarán los reembolsos directamente a este contacto; SuperTicket no gestiona
                                    reembolsos. Para publicar el evento indica al menos uno.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="st-label">Correo para reembolsos</label>
                                        <Input
                                            name="refundEmail"
                                            type="email"
                                            value={eventData.refundEmail}
                                            onChange={handleEventChange}
                                            placeholder="Ej: reembolsos@miempresa.co"
                                        />
                                    </div>
                                    <div>
                                        <label className="st-label">Teléfono para reembolsos</label>
                                        <Input
                                            name="refundPhone"
                                            type="tel"
                                            value={eventData.refundPhone}
                                            onChange={handleEventChange}
                                            placeholder="Ej: 3001234567"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Descripción */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required">Descripción del Evento</label>
                                <textarea
                                    name="description"
                                    value={eventData.description}
                                    onChange={handleEventChange}
                                    rows={4}
                                    className="st-input st-textarea w-full"
                                    placeholder="Detalles, artistas invitados, recomendaciones..."
                                    required
                                />
                            </div>

                            {/* Términos y condiciones */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required">Términos y condiciones legales</label>
                                <textarea
                                    name="termsAndConditions"
                                    value={eventData.termsAndConditions}
                                    onChange={handleEventChange}
                                    rows={4}
                                    className="st-input st-textarea w-full"
                                    placeholder="Políticas de ingreso, edad mínima, devoluciones..."
                                    required
                                />
                            </div>

                            {/* Flyer */}
                            <div className="md:col-span-2">
                                <label className="st-label st-label-required">Flyer Promocional (JPG/PNG)</label>
                                <div className="flex flex-col md:flex-row gap-6 items-start">
                                    <div className="w-full md:w-48 h-64 border-2 border-dashed border-[var(--st-border)] rounded-xl bg-[var(--st-bg-tertiary)] flex items-center justify-center overflow-hidden relative group">
                                        {flyerPreview ? (
                                            <>
                                                <img src={flyerPreview} alt="Flyer Preview" className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                                    <span className="text-white text-xs font-bold">Cambiar Imagen</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center p-4 text-[var(--st-text-muted)]">
                                                <span className="text-sm">Sin imagen</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="st-input p-1 flex items-center justify-between gap-3 group hover:border-[var(--st-accent-primary)] transition-colors mb-2">
                                            <div className="flex items-center gap-3 px-2 overflow-hidden">
                                                <span className={`text-sm truncate ${flyerFile ? 'text-[var(--st-text-primary)] font-medium' : 'text-[var(--st-text-muted)]'}`}>
                                                    {flyerFile ? flyerFile.name : (isEditMode && flyerPreview ? "Imagen actual conservada" : "Selecciona el flyer del evento...")}
                                                </span>
                                            </div>
                                            <label className="st-btn st-btn-secondary st-btn-sm cursor-pointer whitespace-nowrap">
                                                {isEditMode && flyerPreview ? "Cambiar Imagen" : "Subir Imagen"}
                                                <input type="file" accept=".jpg, .jpeg, .png" className="hidden" onChange={handleFlyerChange} />
                                            </label>
                                        </div>
                                        <p className="st-helper-text">Recomendado: Formato vertical 1080x1920px o cuadrado 1080x1080px. Máximo 5MB.</p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* TARJETA 2: ETAPAS */}
                    <div className="st-card-static p-6 md:p-8 bg-[var(--st-bg-tertiary)] border-[var(--st-border)]">
                        <div className="flex items-center justify-between mb-6 border-b border-[var(--st-border)] pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--st-accent-secondary-light)] text-[var(--st-accent-secondary)] flex items-center justify-center font-bold">2</div>
                                <div>
                                    <h2 className="font-display text-xl font-bold">Etapas de Venta</h2>
                                    <p className="text-sm text-[var(--st-text-secondary)]">Define los precios y cantidades por fase.</p>
                                </div>
                            </div>
                            <Button type="button" variant="secondary" size="sm" onClick={addStage}>Agregar Etapa</Button>
                        </div>

                        <div className="space-y-4">
                            {stages.map((stage) => (
                                <div key={stage.id} className="bg-white border border-[var(--st-border)] rounded-xl p-4 shadow-sm relative group">
                                    <button
                                        type="button"
                                        onClick={() => removeStage(stage.id)}
                                        className="absolute top-2 right-2 p-1 text-[var(--st-text-muted)] hover:text-[var(--st-error)] rounded-full"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                        <div className="lg:col-span-1">
                                            <label className="text-xs font-semibold text-[var(--st-text-muted)] uppercase mb-1 block">Nombre Etapa</label>
                                            <input type="text" className="st-input st-input-sm" value={stage.name} onChange={(e) => updateStage(stage.id, 'name', e.target.value)} required placeholder="Ej: Preventa" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[var(--st-text-muted)] uppercase mb-1 block">Precio</label>
                                            <input type="number" className="st-input st-input-sm" value={stage.price} onChange={(e) => updateStage(stage.id, 'price', e.target.value)} required placeholder="0" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[var(--st-text-muted)] uppercase mb-1 block">Cantidad</label>
                                            <input type="number" className="st-input st-input-sm" value={stage.capacity} onChange={(e) => updateStage(stage.id, 'capacity', e.target.value)} required placeholder="100" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[var(--st-text-muted)] uppercase mb-1 block">Cortesías</label>
                                            <input type="number" className="st-input st-input-sm" value={stage.courtesy} onChange={(e) => updateStage(stage.id, 'courtesy', e.target.value)} placeholder="0" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold text-[var(--st-text-muted)] uppercase mb-1 block">Estado</label>
                                            <select className="st-input st-input-sm st-select" value={stage.status} onChange={(e) => updateStage(stage.id, 'status', e.target.value as any)}>
                                                <option value="active">🟢 Activa</option>
                                                <option value="inactive">⚪ Inactiva</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                        <Button type="button" variant="ghost" onClick={() => navigate('/organizer/dashboard')}>
                            Cancelar
                        </Button>

                        <Button
                            type="submit"
                            className="st-btn st-btn-primary st-btn-md"
                            variant={eventData.status === 'ACTIVE' ? 'primary' : 'secondary'}
                            size="lg"
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <><span className="st-spinner st-spinner-sm border-current border-t-transparent"></span> {isEditMode ? 'Actualizando...' : 'Guardando...'}</>
                            ) : (
                                eventData.status === 'ACTIVE'
                                    ? (isEditMode ? 'Actualizar Evento' : 'Publicar Evento')
                                    : (isEditMode ? 'Guardar Cambios' : 'Guardar Borrador')
                            )}
                        </Button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default OrganizerCreateEditEvent;
